import { useAuth } from "#/feature/auth/auth-context";
import { authErrorMessage, changePassword } from "#/feature/auth/auth-api";
import { TotpSetup } from "#/feature/auth/totp-setup";
import { useRequireAuth } from "#/feature/auth/use-require-auth";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BookOpen,
  ChevronRight,
  Ghost,
  KeyRound,
  Moon,
  Replace,
  Settings,
  Shield,
  Sun,
  Volume2,
} from "lucide-react";
import { useTts } from "#/feature/tts-playback/tts-context";
import { type TtsEngine, type TtsVoice } from "#/feature/tts-playback/tts-plugin";
import { SearchReplaceSettings } from "#/feature/replace/replace-settings";
import {
  pageStyles,
  headerStyles,
  titleStyles,
  sectionStyles,
  rowStyles,
  settingsContainerStyles,
  settingsGroupStyles,
  settingsItemStyles,
  settingHeaderStyles,
  settingDescriptionStyles,
  iconBoxStyles,
  labelGroupStyles,
  labelTextStyles,
  valueTextStyles,
  logoutBtnStyles,
  rowButtonStyles,
  chevronStyles,
  selectStyles,
  successMessageStyles,
} from "./user.css";
import {
  buttonStyles,
  errorStyles,
  fieldStyles,
  formStyles,
  labelStyles,
} from "#/feature/auth/login.css";
import { themeSignal, toggleTheme } from "#/feature/theme/theme-store";
import { useSignal } from "#/use-signal";
import { Button } from "#/components/button/button";
import { Input } from "#/components/input/input";
import { Modal } from "#/components/modal/modal";
import { Switch } from "#/components/switch/switch";
import { HelpTooltip } from "#/components/help-tooltip/help-tooltip";

export function UserPage() {
  const allowed = useRequireAuth();
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [totpMode, setTotpMode] = useState<"add" | "update" | null>(null);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [ttsModalOpen, setTtsModalOpen] = useState(false);
  const [replaceModalOpen, setReplaceModalOpen] = useState(false);
  const handleLogout = () => {
    void logout().then(() => navigate("/login"));
  };
  const {
    rate,
    setRate,
    getVoices,
    setVoice,
    getEngines,
    setEngine,
    isTtsSupported,
    sentenceDelay,
    paragraphDelay,
    setSentenceDelay,
    setParagraphDelay,
    inactivityChapterLookback,
    setInactivityChapterLookback,
    engine,
    voice,
    privacyMode,
    setPrivacyMode,
  } = useTts();
  const theme = useSignal(themeSignal);

  if (!allowed) return null;

  return (
    <div className={pageStyles}>
      <div className={headerStyles}>
        <h1 className={titleStyles}>Account</h1>
      </div>

      <div className={settingsContainerStyles}>
        <section className={sectionStyles}>
          <SettingItem
            icon={theme === "light" ? <Sun size={20} /> : <Moon size={20} />}
            label="Appearance"
            value={theme === "light" ? "Light Mode" : "Dark Mode"}
            action={
              <Switch
                aria-label="Toggle Dark Mode"
                checked={theme === "dark"}
                onChange={toggleTheme}
              />
            }
          />

          <SettingItem
            icon={privacyMode ? <Ghost size={20} /> : <BookOpen size={20} />}
            label="Private mode"
            value={privacyMode ? "Enabled" : "Disabled"}
            showHelpIcon
            description="Hide book title & cover in system notification and hide book images and URLs in UI"
            action={
              <Switch
                aria-label="Toggle Private mode"
                checked={privacyMode}
                onChange={() => setPrivacyMode(!privacyMode)}
              />
            }
          />

          <SettingItem
            icon={<Shield size={20} />}
            label="Authenticator"
            value={user?.totp_enabled ? "Enabled" : "Not set up"}
            onClick={() => setTotpMode(user?.totp_enabled ? "update" : "add")}
          />

          <SettingItem
            icon={<KeyRound size={20} />}
            label="Change password"
            subtitle="Update your account password"
            onClick={() => setPasswordModalOpen(true)}
          />

          <SettingItem
            icon={<Volume2 size={20} />}
            label="Audio player"
            value={isTtsSupported ? (engine ? engine : "Not configured") : "Unavailable"}
            subtitle={
              isTtsSupported ? "Engine, voice and playback delays" : "Not supported on this device."
            }
            onClick={isTtsSupported ? () => setTtsModalOpen(true) : undefined}
            disabled={!isTtsSupported}
          />

          <SettingItem
            icon={<Replace size={20} />}
            label="Search & Replace"
            subtitle="Patterns applied to chapter text"
            onClick={() => setReplaceModalOpen(true)}
          />
        </section>

        <Modal
          open={totpMode !== null}
          onClose={() => setTotpMode(null)}
          title={totpMode === "update" ? "Update authenticator" : "Set up authenticator"}
        >
          {totpMode && (
            <TotpSetup
              mode={totpMode}
              showCancel
              onComplete={() => setTotpMode(null)}
              onCancel={() => setTotpMode(null)}
            />
          )}
        </Modal>

        <ChangePasswordModal open={passwordModalOpen} onClose={() => setPasswordModalOpen(false)} />

        <Modal open={ttsModalOpen} onClose={() => setTtsModalOpen(false)} title="Audio player">
          {ttsModalOpen && (
            <TtsSettingsSection
              rate={rate}
              getVoices={getVoices}
              setVoice={setVoice}
              getEngines={getEngines}
              setEngine={setEngine}
              setRate={setRate}
              sentenceDelay={sentenceDelay}
              paragraphDelay={paragraphDelay}
              setSentenceDelay={setSentenceDelay}
              setParagraphDelay={setParagraphDelay}
              inactivityChapterLookback={inactivityChapterLookback}
              setInactivityChapterLookback={setInactivityChapterLookback}
              engine={engine}
              voice={voice}
            />
          )}
        </Modal>

        <Modal
          open={replaceModalOpen}
          onClose={() => setReplaceModalOpen(false)}
          title="Search & Replace"
        >
          {replaceModalOpen && <SearchReplaceSettings />}
        </Modal>

        <div>
          <Button onClick={handleLogout} variant="danger" className={logoutBtnStyles}>
            Logout
          </Button>
        </div>
      </div>
    </div>
  );
}

function TtsSettingsSection({
  rate,
  getVoices,
  setVoice,
  getEngines,
  setEngine,
  setRate,
  sentenceDelay,
  paragraphDelay,
  setSentenceDelay,
  setParagraphDelay,
  inactivityChapterLookback,
  setInactivityChapterLookback,
  engine,
  voice,
}: any) {
  const [voices, setVoicesList] = useState<TtsVoice[]>([]);
  const [engines, setEnginesList] = useState<TtsEngine[]>([]);
  const [loading, setLoading] = useState(false);

  // Local state for inputs to allow typing freely
  const [localSentenceDelay, setLocalSentenceDelay] = useState(sentenceDelay?.toString() ?? "300");
  const [localParagraphDelay, setLocalParagraphDelay] = useState(
    paragraphDelay?.toString() ?? "600",
  );

  const [selectedLanguage, setSelectedLanguage] = useState<string>("");
  const [selectedRegion, setSelectedRegion] = useState<string>("");
  const [selectedNetworkType, setSelectedNetworkType] = useState<string>(
    () =>
      (typeof localStorage !== "undefined"
        ? localStorage.getItem("tts_voice_network_type")
        : null) ?? "",
  );

  useEffect(() => {
    setLocalSentenceDelay(sentenceDelay?.toString() ?? "300");
  }, [sentenceDelay]);

  useEffect(() => {
    setLocalParagraphDelay(paragraphDelay?.toString() ?? "600");
  }, [paragraphDelay]);

  // Load engines once on mount.
  useEffect(() => {
    let cancelled = false;
    getEngines()
      .then((e: TtsEngine[]) => {
        if (!cancelled) {
          setEnginesList(Array.isArray(e) ? e : []);
        }
      })
      .catch(() => {
        if (!cancelled) setEnginesList([]);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload voices whenever the selected engine changes.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getVoices()
      .then((v: TtsVoice[]) => {
        if (cancelled) return;
        const safeVoices = Array.isArray(v) ? v : [];
        setVoicesList(safeVoices);

        if (voice) {
          const selected = safeVoices.find((vx: TtsVoice) => vx.name === voice);
          if (selected) {
            setSelectedLanguage(selected.language || "");
            setSelectedRegion(selected.region || "");
            const savedNetwork = localStorage.getItem("tts_voice_network_type");
            if (savedNetwork === null && selected.isNetwork) {
              setSelectedNetworkType(selected.isNetwork);
            }
          } else if (safeVoices.length > 0) {
            const first = safeVoices[0];
            if (first) setSelectedLanguage(first.language || "");
            setSelectedRegion("");
          }
        } else if (safeVoices.length > 0) {
          const first = safeVoices[0];
          if (first) setSelectedLanguage(first.language || "");
        }

        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine]);

  const handleEngineChange = (engineId: string) => {
    void setEngine(engineId);
  };

  const handleDelayBlur = (val: string, setter: (v: number) => void, fallback: number) => {
    const num = parseInt(val, 10);
    if (!isNaN(num) && num >= 0) {
      setter(num);
    } else {
      setter(fallback);
      if (setter === setSentenceDelay) setLocalSentenceDelay(fallback.toString());
      if (setter === setParagraphDelay) setLocalParagraphDelay(fallback.toString());
    }
  };

  // Calculate unique languages and regions from the parsed data
  const safeVoicesList = Array.isArray(voices) ? voices : [];
  const languageOptions = Array.from(
    new Map(safeVoicesList.map((v) => [v.language, v.displayLanguage])).entries(),
  )
    .filter(([lang]) => lang)
    .sort((a, b) => (a[1] || "").localeCompare(b[1] || ""));

  const regionsForLanguageOptions = Array.from(
    new Map(
      safeVoicesList
        .filter((v) => v.language === selectedLanguage)
        .map((v) => [v.region || "", v.displayRegion || ""]),
    ).entries(),
  ).sort((a, b) => (a[1] || "").localeCompare(b[1] || ""));

  const filteredVoices = safeVoicesList.filter((v) => {
    const matchesLang = (v.language || "") === selectedLanguage;
    const matchesRegion = selectedRegion === "" || (v.region || "") === selectedRegion;
    const matchesNetwork = selectedNetworkType === "" || v.isNetwork === selectedNetworkType;
    return matchesLang && matchesRegion && matchesNetwork;
  });

  return (
    <div className={settingsGroupStyles}>
      <div className={settingsItemStyles}>
        <div className={settingHeaderStyles}>
          <Volume2 size={16} style={{ color: "var(--colors-primary)" }} /> Playback Speed
        </div>
        <select
          className={selectStyles}
          value={rate}
          onChange={(e) => setRate(parseFloat(e.target.value))}
        >
          {[0.5, 0.75, 1, 1.25, 1.5, 2, 2.5, 3].map((r) => (
            <option key={r} value={r}>
              {r}x
            </option>
          ))}
        </select>
      </div>

      <div className={settingsItemStyles}>
        <div className={settingHeaderStyles}>
          <Settings size={16} style={{ color: "var(--colors-primary)" }} /> Inactivity Chapter
          Lookback
        </div>
        <select
          className={selectStyles}
          value={inactivityChapterLookback ?? 2}
          onChange={(e) => setInactivityChapterLookback(parseInt(e.target.value, 10))}
        >
          <option value={0}>Never</option>
          <option value={1}>1 chapter</option>
          <option value={2}>2 chapters</option>
          <option value={3}>3 chapters</option>
          <option value={5}>5 chapters</option>
          <option value={10}>10 chapters</option>
        </select>
      </div>

      <div className={settingsItemStyles}>
        <div className={settingHeaderStyles}>
          <Settings size={16} style={{ color: "var(--colors-primary)" }} /> Sentence Delay (ms)
        </div>
        <input
          type="number"
          className={selectStyles}
          value={localSentenceDelay}
          onChange={(e) => setLocalSentenceDelay(e.target.value)}
          onBlur={(e) => handleDelayBlur(e.target.value, setSentenceDelay, sentenceDelay ?? 300)}
        />
      </div>

      <div className={settingsItemStyles}>
        <div className={settingHeaderStyles}>
          <Settings size={16} style={{ color: "var(--colors-primary)" }} /> Paragraph Delay (ms)
        </div>
        <input
          type="number"
          className={selectStyles}
          value={localParagraphDelay}
          onChange={(e) => setLocalParagraphDelay(e.target.value)}
          onBlur={(e) => handleDelayBlur(e.target.value, setParagraphDelay, paragraphDelay ?? 600)}
        />
      </div>

      <div className={settingsItemStyles}>
        <div className={settingHeaderStyles}>
          <Settings size={16} style={{ color: "var(--colors-primary)" }} /> TTS Engine
        </div>
        <select
          className={selectStyles}
          value={engine || ""}
          onChange={(e) => handleEngineChange(e.target.value)}
        >
          <option disabled value="">
            Select Engine
          </option>
          {engines.map((e) => (
            <option key={e.name} value={e.name}>
              {e.label}
            </option>
          ))}
        </select>
      </div>

      <div className={settingsItemStyles}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
          <Volume2 size={16} style={{ color: "var(--colors-primary)" }} /> Voice Filters
        </div>

        <div style={{ display: "flex", gap: "10px", marginBottom: "10px", flexWrap: "wrap" }}>
          <select
            className={selectStyles}
            value={selectedLanguage}
            onChange={(e) => {
              setSelectedLanguage(e.target.value);
              setSelectedRegion("");
            }}
          >
            <option disabled value="">
              Language
            </option>
            {languageOptions.map(([langCode, langName]) => (
              <option key={langCode} value={langCode}>
                {langName}
              </option>
            ))}
          </select>

          <select
            className={selectStyles}
            value={selectedRegion}
            onChange={(e) => setSelectedRegion(e.target.value)}
          >
            <option value="">All Regions</option>
            {regionsForLanguageOptions.map(([regCode, regName]) =>
              regCode ? (
                <option key={regCode} value={regCode}>
                  {regName}
                </option>
              ) : null,
            )}
          </select>

          <select
            className={selectStyles}
            value={selectedNetworkType}
            onChange={(e) => {
              const val = e.target.value;
              setSelectedNetworkType(val);
              localStorage.setItem("tts_voice_network_type", val);
            }}
          >
            <option value="">All Types</option>
            <option value="true">Network</option>
            <option value="false">Local</option>
          </select>
        </div>

        <select
          className={selectStyles}
          value={voice || ""}
          onChange={(e) => setVoice(e.target.value)}
        >
          <option disabled value="">
            Select Voice
          </option>
          {filteredVoices.map((v) => (
            <option key={v.name} value={v.name}>
              {v.label}
            </option>
          ))}
        </select>
      </div>

      {loading && (
        <div style={{ textAlign: "center", color: "var(--colors-primary)" }}>Loading...</div>
      )}
    </div>
  );
}

function SettingItem({
  icon,
  label,
  value,
  subtitle,
  description,
  showHelpIcon,
  action,
  onClick,
  disabled,
}: {
  icon: React.ReactNode;
  label: string;
  value?: string;
  subtitle?: string;
  description?: string;
  showHelpIcon?: boolean;
  action?: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}) {
  const isButton = Boolean(onClick) || Boolean(disabled);
  const inner = (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", minWidth: 0 }}>
        <div className={iconBoxStyles}>{icon}</div>
        <div className={labelGroupStyles}>
          <div className={labelTextStyles}>{label}</div>
          {(value || showHelpIcon) && (
            <div style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
              {value && <div className={valueTextStyles}>{value}</div>}
              {showHelpIcon && description && <HelpTooltip description={description} />}
            </div>
          )}
          {subtitle && <div className={settingDescriptionStyles}>{subtitle}</div>}
        </div>
      </div>
      {action ? (
        <div style={{ flexShrink: 0 }}>{action}</div>
      ) : isButton ? (
        <ChevronRight size={18} className={chevronStyles} />
      ) : null}
    </>
  );

  if (isButton) {
    return (
      <button type="button" className={rowButtonStyles} onClick={onClick} disabled={disabled}>
        {inner}
      </button>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", position: "relative" }}>
      <div className={rowStyles}>{inner}</div>
    </div>
  );
}

function ChangePasswordModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const handleClose = () => {
    setPasswordError("");
    setPasswordSuccess("");
    setOldPassword("");
    setNewPassword("");
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} title="Change password">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setPasswordError("");
          setPasswordSuccess("");
          void changePassword(oldPassword, newPassword)
            .then(() => {
              setPasswordSuccess("Password updated");
              setOldPassword("");
              setNewPassword("");
            })
            .catch((err) => setPasswordError(authErrorMessage(err, "Failed to change password")));
        }}
        className={formStyles}
      >
        {passwordError && <div className={errorStyles}>{passwordError}</div>}
        {passwordSuccess && <div className={successMessageStyles}>{passwordSuccess}</div>}
        <div className={fieldStyles}>
          <label className={labelStyles}>Current password</label>
          <Input
            type="password"
            placeholder="Current password"
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            required
          />
        </div>
        <div className={fieldStyles}>
          <label className={labelStyles}>New password</label>
          <Input
            type="password"
            placeholder="New password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
        </div>
        <Button type="submit" fullWidth className={buttonStyles}>
          Update password
        </Button>
        <Button type="button" variant="outline" fullWidth onClick={handleClose}>
          Cancel
        </Button>
      </form>
    </Modal>
  );
}
